import React, { FC } from 'react';
import { Controller, Control } from 'react-hook-form';
import { AU_MOBILE_PATTERN, EMAIL_PATTERN, POSTCODE_PATTERN } from '../utils/details-validation';
import type { PatientInfoValues } from '../types/flow';
import {
  FormInput, FormSelect, FLabel,
} from '../../assessment-portal/components/form-fields';
import {
  AUSTRALIAN_STATES,
  GENDER_OPTIONS,
  CULTURAL_BACKGROUND_OPTIONS,
  REFERRAL_SOURCE_OPTIONS,
} from '../data/constants';

interface PersonalContactFormProps {
  control: Control<PatientInfoValues>;
}

const PersonalContactForm: FC<PersonalContactFormProps> = ({
  control,
}) => (
  <>
    <h2 className="text-xl font-bold mb-1 text-brand-dark">Personal &amp; Contact</h2>
    <p className="text-slate-500 text-sm mb-5">Step 1 of 2 — your basic details and address.</p>

    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <FLabel isRequired>First Name</FLabel>
          <Controller
            name="firstName"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="First name" autoComplete="given-name" />}
          />
        </div>
        <div>
          <FLabel isRequired>Last Name</FLabel>
          <Controller
            name="lastName"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Last name" autoComplete="family-name" />}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="min-w-0">
          <FLabel isRequired>Date of Birth</FLabel>
          <Controller
            name="dateOfBirth"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} type="date" inputMode="numeric" className="min-w-0" />}
          />
        </div>
        <div>
          <FLabel isRequired>Gender</FLabel>
          <Controller
            name="gender"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => (
              <FormSelect ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur}>
                {GENDER_OPTIONS.map((g) => <option key={g}>{g}</option>)}
              </FormSelect>
            )}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <FLabel isRequired>Email</FLabel>
          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Email is required',
              pattern: { value: EMAIL_PATTERN, message: 'Please enter a valid email address' },
            }}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
              fieldState: { error },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} type="email" placeholder="you@email.com" inputMode="email" autoComplete="email" error={error?.message} />}
          />
        </div>
        <div>
          <FLabel isRequired>Mobile</FLabel>
          <Controller
            name="mobile"
            control={control}
            rules={{
              required: 'Mobile number is required',
              pattern: { value: AU_MOBILE_PATTERN, message: 'Please enter a valid Australian mobile (04XXXXXXXX)' },
            }}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
              fieldState: { error },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="04XX XXX XXX" inputMode="tel" autoComplete="tel" error={error?.message} />}
          />
        </div>
      </div>

      <div>
        <FLabel isRequired>Street Address</FLabel>
        <Controller
          name="address"
          control={control}
          render={({
            field: {
              ref, name, value, onChange, onBlur,
            },
          }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Street address" />}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <FLabel isRequired>Suburb</FLabel>
          <Controller
            name="suburb"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="Suburb" />}
          />
        </div>
        <div>
          <FLabel isRequired>State</FLabel>
          <Controller
            name="state"
            control={control}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
            }) => (
              <FormSelect ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur}>
                {AUSTRALIAN_STATES.map((s) => <option key={s}>{s}</option>)}
              </FormSelect>
            )}
          />
        </div>
        <div>
          <FLabel isRequired>Postcode</FLabel>
          <Controller
            name="postcode"
            control={control}
            rules={{
              required: 'Postcode is required',
              pattern: { value: POSTCODE_PATTERN, message: 'Postcode must be 4 digits' },
            }}
            render={({
              field: {
                ref, name, value, onChange, onBlur,
              },
              fieldState: { error },
            }) => <FormInput ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur} placeholder="XXXX" inputMode="numeric" error={error?.message} />}
          />
        </div>
      </div>

      <div>
        <FLabel isRequired>Cultural Background</FLabel>
        <Controller
          name="cultural"
          control={control}
          render={({
            field: {
              ref, name, value, onChange, onBlur,
            },
          }) => (
            <FormSelect ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur}>
              {CULTURAL_BACKGROUND_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </FormSelect>
          )}
        />
      </div>

      <div>
        <FLabel isRequired>How did you hear about us?</FLabel>
        <Controller
          name="referral"
          control={control}
          render={({
            field: {
              ref, name, value, onChange, onBlur,
            },
          }) => (
            <FormSelect ref={ref} name={name} value={value} onChange={onChange} onBlur={onBlur}>
              {REFERRAL_SOURCE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
            </FormSelect>
          )}
        />
      </div>
    </div>
  </>
);

export default PersonalContactForm;
